import { useEffect, useState } from 'react';
import {
  addMat,
  createTournament,
  loadTournaments,
  removeMat,
  saveMat,
  saveTournament,
  setTournamentStatus,
  type TournamentListState,
  type TournamentRecord,
} from '../tournament-api';

type Role = 'admin' | 'staff';
type View = 'active' | 'archived';

export function TournamentsPanel({ role }: { role: Role }) {
  const [view, setView] = useState<View>('active');
  const [list, setList] = useState<TournamentListState | { kind: 'loading' }>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadTournaments(view).then((next) => {
      if (!cancelled) setList(next);
    });
    return () => {
      cancelled = true;
    };
  }, [view]);

  async function reload(): Promise<void> {
    setList(await loadTournaments(view));
  }

  async function create(event: { preventDefault(): void }): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    const error = await createTournament({
      name,
      slug,
      starts_on: startsOn || null,
      ends_on: endsOn || null,
    });
    setBusy(false);
    if (error) {
      setMessage(error);
      return;
    }
    setName('');
    setSlug('');
    setSlugEdited(false);
    setStartsOn('');
    setEndsOn('');
    await reload();
  }

  function show(next: View): void {
    setMessage('');
    if (next !== view) setList({ kind: 'loading' });
    setView(next);
  }

  return (
    <section>
      <h2>Tournaments</h2>
      <p className="hint">Dates are calendar days in America/Chicago, not times.</p>
      <div className="actions">
        <button className="button" type="button" aria-pressed={view === 'active'} onClick={() => show('active')}>
          Active
        </button>
        <button className="button" type="button" aria-pressed={view === 'archived'} onClick={() => show('archived')}>
          History
        </button>
      </div>
      {role === 'admin' && view === 'active' ? (
        <form onSubmit={(event) => void create(event)}>
          <label htmlFor="tournament-name">Name</label>
          <input
            id="tournament-name"
            type="text"
            maxLength={120}
            autoComplete="off"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugEdited) setSlug(suggestSlug(next));
            }}
            required
          />
          <label htmlFor="tournament-slug">Slug</label>
          <input
            id="tournament-slug"
            type="text"
            maxLength={64}
            autoComplete="off"
            value={slug}
            onChange={(event) => {
              setSlugEdited(true);
              setSlug(event.target.value);
            }}
            required
          />
          <label htmlFor="tournament-starts">Starts</label>
          <input id="tournament-starts" type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
          <label htmlFor="tournament-ends">Ends</label>
          <input id="tournament-ends" type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
          <button className="button" type="submit" disabled={busy}>
            Create tournament
          </button>
        </form>
      ) : null}
      {message ? <p className="note">{message}</p> : null}
      {list.kind === 'loading' ? <p>Loading tournaments…</p> : null}
      {list.kind === 'signed-out' ? <p>Sign in again to see tournaments.</p> : null}
      {list.kind === 'error' ? <p className="note">Tournaments could not be loaded.</p> : null}
      {list.kind === 'ready' && list.tournaments.length === 0 ? (
        <p>{view === 'active' ? 'No active tournaments.' : 'No archived tournaments.'}</p>
      ) : null}
      {list.kind === 'ready'
        ? list.tournaments.map((tournament) => (
            <TournamentCard
              key={cardKey(tournament)}
              tournament={tournament}
              role={role}
              busy={busy}
              setBusy={setBusy}
              onMessage={setMessage}
              onReload={reload}
            />
          ))
        : null}
    </section>
  );
}

function TournamentCard({
  tournament,
  role,
  busy,
  setBusy,
  onMessage,
  onReload,
}: {
  tournament: TournamentRecord;
  role: Role;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onMessage: (message: string) => void;
  onReload: () => Promise<void>;
}) {
  const [name, setName] = useState(tournament.name);
  const [slug, setSlug] = useState(tournament.slug);
  const [startsOn, setStartsOn] = useState(tournament.starts_on ?? '');
  const [endsOn, setEndsOn] = useState(tournament.ends_on ?? '');
  const [number, setNumber] = useState(String(nextNumber(tournament)));
  const [matName, setMatName] = useState('');

  async function saveDetails(event: { preventDefault(): void }): Promise<void> {
    event.preventDefault();
    setBusy(true);
    onMessage('');
    const error = await saveTournament(tournament.id, {
      name,
      slug,
      starts_on: startsOn || null,
      ends_on: endsOn || null,
    });
    setBusy(false);
    if (error) {
      onMessage(error);
      return;
    }
    await onReload();
  }

  async function setStatus(status: 'active' | 'archived'): Promise<void> {
    const question =
      status === 'archived'
        ? `Archive ${tournament.name}? It leaves the active list. Mats stay in history.`
        : `Restore ${tournament.name} to the active list?`;
    if (!window.confirm(question)) return;
    setBusy(true);
    onMessage('');
    const error = await setTournamentStatus(tournament.id, status);
    setBusy(false);
    if (error) {
      onMessage(error);
      return;
    }
    await onReload();
  }

  async function createMat(event: { preventDefault(): void }): Promise<void> {
    event.preventDefault();
    const parsed = Number(number);
    setBusy(true);
    onMessage('');
    const error = await addMat(tournament.id, { number: parsed, name: matName.trim() });
    setBusy(false);
    if (error) {
      onMessage(error);
      return;
    }
    setMatName('');
    await onReload();
  }

  return (
    <section className="tournament">
      <h3>{tournament.name}</h3>
      <p>
        {tournament.status === 'active' ? 'Active' : 'Archived'}
        {' · '}
        {formatDay(tournament.starts_on)} – {formatDay(tournament.ends_on)}
      </p>
      {role === 'admin' ? (
        <form onSubmit={(event) => void saveDetails(event)}>
          <label htmlFor={`name-${tournament.id}`}>Name</label>
          <input
            id={`name-${tournament.id}`}
            type="text"
            maxLength={120}
            autoComplete="off"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <label htmlFor={`slug-${tournament.id}`}>Slug</label>
          <input
            id={`slug-${tournament.id}`}
            type="text"
            maxLength={64}
            autoComplete="off"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            required
          />
          <label htmlFor={`starts-${tournament.id}`}>Starts</label>
          <input
            id={`starts-${tournament.id}`}
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
          <label htmlFor={`ends-${tournament.id}`}>Ends</label>
          <input id={`ends-${tournament.id}`} type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
          <div className="actions">
            <button className="button" type="submit" disabled={busy}>
              Save tournament
            </button>
            {tournament.status === 'active' ? (
              <button className="button" type="button" disabled={busy} onClick={() => void setStatus('archived')}>
                Archive
              </button>
            ) : (
              <button className="button" type="button" disabled={busy} onClick={() => void setStatus('active')}>
                Restore
              </button>
            )}
          </div>
        </form>
      ) : (
        <p>
          Slug {tournament.slug}. Staff can change mats. Only an Admin can change the tournament.
        </p>
      )}
      {tournament.mats.length === 0 ? <p>No mats yet.</p> : null}
      {tournament.mats.map((mat) => (
        <MatEditor
          key={mat.id}
          tournamentId={tournament.id}
          mat={mat}
          busy={busy}
          setBusy={setBusy}
          onMessage={onMessage}
          onReload={onReload}
        />
      ))}
      <form onSubmit={(event) => void createMat(event)}>
        <label htmlFor={`add-number-${tournament.id}`}>Mat number</label>
        <input
          id={`add-number-${tournament.id}`}
          type="number"
          min={1}
          max={30}
          required
          value={number}
          onChange={(event) => setNumber(event.target.value)}
        />
        <label htmlFor={`add-name-${tournament.id}`}>Mat name</label>
        <input
          id={`add-name-${tournament.id}`}
          type="text"
          maxLength={80}
          autoComplete="off"
          placeholder={number ? `Mat ${number}` : 'Mat'}
          value={matName}
          onChange={(event) => setMatName(event.target.value)}
        />
        <button className="button" type="submit" disabled={busy}>
          Add mat
        </button>
      </form>
    </section>
  );
}

function MatEditor({
  tournamentId,
  mat,
  busy,
  setBusy,
  onMessage,
  onReload,
}: {
  tournamentId: string;
  mat: TournamentRecord['mats'][number];
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onMessage: (message: string) => void;
  onReload: () => Promise<void>;
}) {
  const [name, setName] = useState(mat.name);
  const [bracketUrl, setBracketUrl] = useState(mat.bracket_url ?? '');

  async function save(event: { preventDefault(): void }): Promise<void> {
    event.preventDefault();
    setBusy(true);
    onMessage('');
    const error = await saveMat(tournamentId, mat.id, { name, bracket_url: bracketUrl.trim() });
    setBusy(false);
    if (error) {
      onMessage(error);
      return;
    }
    await onReload();
  }

  async function remove(): Promise<void> {
    if (!window.confirm(`Remove ${mat.name}? The other mats keep their numbers.`)) return;
    setBusy(true);
    onMessage('');
    const error = await removeMat(tournamentId, mat.id);
    setBusy(false);
    if (error) {
      onMessage(error);
      return;
    }
    await onReload();
  }

  return (
    <form className="mat-editor" onSubmit={(event) => void save(event)}>
      <h4>Mat {mat.number}</h4>
      <p>
        Bookmark <a href={mat.path}><code>{mat.path}</code></a>
      </p>
      <label htmlFor={`mat-name-${mat.id}`}>Name</label>
      <input
        id={`mat-name-${mat.id}`}
        type="text"
        maxLength={80}
        autoComplete="off"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />
      <label htmlFor={`mat-url-${mat.id}`}>Board link</label>
      <input
        id={`mat-url-${mat.id}`}
        type="text"
        inputMode="url"
        maxLength={2000}
        autoComplete="off"
        placeholder="https://"
        value={bracketUrl}
        onChange={(event) => setBracketUrl(event.target.value)}
      />
      <div className="actions">
        <button className="button" type="submit" disabled={busy}>
          Save mat
        </button>
        <button className="button" type="button" disabled={busy} onClick={() => void remove()}>
          Remove
        </button>
      </div>
    </form>
  );
}

function cardKey(tournament: TournamentRecord): string {
  const mats = tournament.mats
    .map((mat) => `${mat.id}:${mat.number}:${mat.name}:${mat.bracket_url ?? ''}`)
    .join('|');
  return [
    tournament.id,
    tournament.name,
    tournament.slug,
    tournament.starts_on ?? '',
    tournament.ends_on ?? '',
    tournament.status,
    mats,
  ].join(':');
}

function suggestSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '');
}

function nextNumber(tournament: TournamentRecord): number {
  const taken = new Set(tournament.mats.map((mat) => mat.number));
  for (let number = 1; number <= 30; number += 1) {
    if (!taken.has(number)) return number;
  }
  return 30;
}

function formatDay(iso: string | null): string {
  if (!iso) return 'Not set';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match?.[1] || !match[2] || !match[3]) return iso;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}
